import React, { useState, useEffect, useRef, useCallback } from "react";

// Full-screen sequential product tour. Dimmer + centered modal card
// + progress dots + Next/Back/Skip.  Accepts a `steps` array (see
// constants/ftueSteps.js for the shape) so it can drive any role's
// FTUE from one component.
//
// Design rules applied (ui-ux-pro-max):
//   §1 accessibility — role="dialog", aria-modal, focus trap on the
//     primary button, ESC to close, prefers-reduced-motion support,
//     decorative SVGs marked aria-hidden.
//   §2 touch targets — Next button is full-width on mobile; Back /
//     Skip are 44pt+ tap targets with explicit padding.
//   §4 style match — refined-minimal: single brand-tinted icon
//     tile, subtle shadow, no decorative flourish that would
//     distract from the actual content.
//   §7 animation — fade + small upward slide on each step change
//     (interruptible via the next-click; respects reduced-motion).
//   §8 forms/feedback — skip is a low-emphasis link, Next is the
//     primary CTA, progress dots show where the user is.
//
// Props:
//   open: boolean — controls visibility
//   steps: array of { icon, color, title, body, cta? }
//   onComplete: () => void — fires when user finishes the last step
//   onSkip:     () => void — fires when user dismisses early
//   h: theme tokens object
//   t?: i18n strings (optional, falls back to English)
export default function ProductTour({ open, steps, onComplete, onSkip, h = {}, t = {} }) {
  const [step, setStep] = useState(0);
  const primaryBtnRef = useRef(null);

  // Reset step counter every time the tour opens — so closing &
  // re-opening (e.g. from a "Show tour" menu item later) starts
  // fresh instead of resuming half-way.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Auto-focus the primary CTA on mount + every step change so
  // keyboard users can hit Enter to advance.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      try { primaryBtnRef.current?.focus(); } catch (_) { /* ignore */ }
    });
    return () => cancelAnimationFrame(id);
  }, [open, step]);

  const close = useCallback(() => {
    if (onSkip) onSkip();
  }, [onSkip]);

  const next = useCallback(() => {
    if (!steps || steps.length === 0) return;
    if (step >= steps.length - 1) {
      if (onComplete) onComplete();
    } else {
      setStep(s => s + 1);
    }
  }, [step, steps, onComplete]);

  const back = useCallback(() => {
    setStep(s => Math.max(0, s - 1));
  }, []);

  // Keyboard shortcuts:
  //   ESC      → skip the tour
  //   →, Enter → advance
  //   ←        → go back
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); back(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, back, close]);

  if (!open || !steps || steps.length === 0) return null;

  const s = steps[Math.min(step, steps.length - 1)];
  const isFirst = step === 0;
  const isLast = step >= steps.length - 1;
  const ac = h.ac || "#3b82f6";
  const bg = h.bg || "#f5f5f7";
  const cardBg = h.cd || "#ffffff";
  const tx = h.tx || "#111114";
  const sc = h.sc || "#8e8e93";
  const bd = h.bd || "rgba(0,0,0,0.08)";
  const stepColor = s.color || ac;

  // Default button labels — overridden per-step via s.cta if provided.
  const nextLabel = s.cta || (isLast ? (t.finish || "Finish") : (t.next || "Next"));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ftue-title"
      aria-describedby="ftue-body"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(8, 10, 22, 0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <style>{`
        @keyframes ftue-card-in {
          from { opacity: 0; transform: translateY(8px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes ftue-halo {
          0%, 100% { transform: scale(1);   opacity: 0.45; }
          50%      { transform: scale(1.18); opacity: 0;   }
        }
        @media (prefers-reduced-motion: reduce) {
          .ftue-anim { animation: none !important; }
        }
      `}</style>

      <div
        key={step}
        className="ftue-anim"
        style={{
          background: cardBg,
          borderRadius: 20,
          padding: "28px 24px 22px",
          maxWidth: 360,
          width: "100%",
          boxShadow: "0 18px 48px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.06)",
          border: `1px solid ${bd}`,
          textAlign: "center",
          position: "relative",
          animation: "ftue-card-in 0.32s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Top-right skip — discoverable but not loud */}
        <button
          onClick={close}
          aria-label="Skip tour"
          title="Skip"
          style={{
            position: "absolute",
            top: 12, right: 12,
            background: "transparent",
            border: "none",
            color: sc,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            padding: "8px 10px",
            borderRadius: 8,
            lineHeight: 1,
          }}
        >
          Skip
        </button>

        {/* Icon tile with breathing halo — colored per step so the
            visual signal of "we moved on" is unmistakable. */}
        <div style={{
          position: "relative",
          width: 80, height: 80,
          margin: "4px auto 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <span aria-hidden="true" className="ftue-anim" style={{
            position: "absolute", inset: 0,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${stepColor}55 0%, ${stepColor}00 65%)`,
            animation: "ftue-halo 2.6s ease-out infinite",
          }} />
          <div style={{
            position: "relative",
            width: 60, height: 60,
            borderRadius: 16,
            background: `linear-gradient(135deg, ${stepColor} 0%, ${stepColor}dd 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 10px 24px ${stepColor}40, inset 0 1px 0 rgba(255,255,255,0.22)`,
          }}>
            <svg
              width="28" height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {s.icon}
            </svg>
          </div>
        </div>

        {/* Step label */}
        <div style={{
          color: sc,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          marginBottom: 6,
        }}>
          Step {step + 1} of {steps.length}
        </div>

        <h2
          id="ftue-title"
          style={{
            color: tx,
            fontSize: 20,
            fontWeight: 700,
            margin: "0 0 8px",
            letterSpacing: "-0.3px",
            lineHeight: 1.25,
          }}
        >
          {s.title}
        </h2>

        <p
          id="ftue-body"
          style={{
            color: sc,
            fontSize: 13.5,
            lineHeight: 1.5,
            margin: "0 0 24px",
            maxWidth: 300,
            marginLeft: "auto", marginRight: "auto",
          }}
        >
          {s.body}
        </p>

        {/* Progress dots */}
        <div
          role="progressbar"
          aria-valuenow={step + 1}
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-label={`Tour progress, step ${step + 1} of ${steps.length}`}
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 6,
            marginBottom: 22,
          }}
        >
          {steps.map((_, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <span
                key={i}
                aria-hidden="true"
                style={{
                  width: active ? 22 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: active ? stepColor : done ? `${stepColor}55` : (bd || "#e5e5e7"),
                  transition: "all 0.3s ease",
                }}
              />
            );
          })}
        </div>

        {/* Nav row — Back (left, low-emphasis) + Next (full-width
            primary on mobile so the tap target is huge) */}
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          {!isFirst && (
            <button
              onClick={back}
              aria-label="Previous step"
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${bd}`,
                background: "transparent",
                color: tx,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                minWidth: 44,
                minHeight: 44,
              }}
            >
              ‹ Back
            </button>
          )}
          <button
            ref={primaryBtnRef}
            onClick={next}
            style={{
              flex: 1,
              padding: "12px 16px",
              borderRadius: 12,
              border: "none",
              background: stepColor,
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.1px",
              cursor: "pointer",
              boxShadow: `0 6px 18px ${stepColor}50`,
              minHeight: 44,
              transition: "transform 0.1s ease",
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          >
            {nextLabel}{!isLast && " →"}
          </button>
        </div>
      </div>
    </div>
  );
}
